import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';

interface EmissionProgressChartProps {
  data: {
    scope1?: {
      current: number;
      target: number;
      baseline: number;
    };
    scope2?: {
      current: number;
      target: number;
      baseline: number;
    };
    scope3?: {
      current: number;
      target: number;
      baseline: number;
    };
    total?: {
      current: number;
      target: number;
      baseline: number;
    };
  };
}

export function EmissionProgressChart({ data }: EmissionProgressChartProps) {
  // Create mock data if no real data is provided
  const mockData = {
    scope1: {
      current: 20444,
      target: 15000, // 25% reduction target
      baseline: 25000
    },
    scope2: {
      current: 81000,
      target: 70000, // 15% reduction target
      baseline: 90000
    },
    scope3: {
      current: 112300,
      target: 100000, // 10% reduction target
      baseline: 120000
    },
    total: {
      current: 213744,
      target: 185000, // 15% overall reduction target
      baseline: 235000
    }
  };

  const progressData = data || mockData;

  // Transform data for the chart
  const chartData = [
    {
      name: 'Scope 1',
      Current: progressData.scope1?.current || 0,
      Target: progressData.scope1?.target || 0,
      Baseline: progressData.scope1?.baseline || 0,
      reduction: progressData.scope1 ? 
        ((progressData.scope1.baseline - progressData.scope1.current) / progressData.scope1.baseline * 100) : 0
    },
    {
      name: 'Scope 2',
      Current: progressData.scope2?.current || 0,
      Target: progressData.scope2?.target || 0,
      Baseline: progressData.scope2?.baseline || 0,
      reduction: progressData.scope2 ? 
        ((progressData.scope2.baseline - progressData.scope2.current) / progressData.scope2.baseline * 100) : 0
    },
    {
      name: 'Scope 3',
      Current: progressData.scope3?.current || 0,
      Target: progressData.scope3?.target || 0,
      Baseline: progressData.scope3?.baseline || 0,
      reduction: progressData.scope3 ? 
        ((progressData.scope3.baseline - progressData.scope3.current) / progressData.scope3.baseline * 100) : 0
    },
    {
      name: 'Total',
      Current: progressData.total?.current || 0,
      Target: progressData.total?.target || 0,
      Baseline: progressData.total?.baseline || 0,
      reduction: progressData.total ? 
        ((progressData.total.baseline - progressData.total.current) / progressData.total.baseline * 100) : 0
    },
  ];

  const formatYAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toFixed(0);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="p-3 bg-white border border-gray-300 rounded-md shadow-md text-sm">
          <p className="font-bold text-gray-800 mb-2">{label} Emissions</p>
          <div className="space-y-1">
            <p className="text-gray-600">
              <span className="font-medium">Current:</span> {data.Current.toFixed(0)} kg CO₂e
            </p>
            <p className="text-blue-600">
              <span className="font-medium">Target:</span> {data.Target.toFixed(0)} kg CO₂e
            </p>
            <p className="text-gray-500">
              <span className="font-medium">Baseline:</span> {data.Baseline.toFixed(0)} kg CO₂e
            </p>
            <p className={`font-medium ${data.reduction >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              <span>Reduction:</span> {data.reduction.toFixed(1)}%
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full min-w-0">
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          margin={{
            top: 16,
            right: 16,
            left: 0,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={48} tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          <Bar dataKey="Baseline" fill="#e5e7eb" name="Baseline" opacity={0.6} />
          <Bar dataKey="Current" fill="#3b82f6" name="Current Year" />
          <Bar dataKey="Target" fill="#22c55e" name="Target" />
        </BarChart>
      </ResponsiveContainer>
      
      {/* Progress Summary */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {chartData.map((item, index) => (
          <div key={index} className="text-center">
            <div className="text-sm font-medium text-gray-600">{item.name}</div>
            <div className={`text-lg font-bold ${item.reduction >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.reduction >= 0 ? '↓' : '↑'} {Math.abs(item.reduction).toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500">
              {item.reduction >= 0 ? 'Reduction' : 'Increase'}
            </div>
          </div>
        ))}
      </div>
      
      {/* Demo Mode Notice */}
      {!data && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
          <i data-feather="info" className="w-3 h-3 inline mr-1"></i>
          Demo Mode: Showing sample progress data with 10-25% reduction targets
        </div>
      )}
    </div>
  );
}



